/**
 * @fileoverview Optimization theory drawer — the method behind the Optimization
 * tab: find the weakest room, derive the required supply temperature from the
 * EN 442 radiator law, and size each room's flow. Mirrors the ScoresDrawer.
 *
 * @module HydraulicBalancingTesting/components/OptimizationTheoryDrawer
 */

import React from 'react';
import { Drawer } from 'antd';

import { LatexRenderer } from '../../../Shared/LatexRenderer';

import styles from '../HydraulicBalancingTesting.module.css';

const BLOCKS = [
  { type: 'text', text: 'Goal: hold every room at its setpoint with the lowest sufficient supply temperature (best COP) and correctly sized flows. The optimizer works in four steps.' },

  { type: 'subtitle', text: '1 · Find the weakest room' },
  { type: 'text', text: 'For each room compare what its radiator currently delivers with the heat it loses at setpoint:' },
  { type: 'latex', latex: '$ \\eta_i = \\dfrac{\\dot Q_{rad,i}}{\\dot Q_{loss,i}} $' },
  { type: 'text', text: 'η < 1 means the radiator cannot keep up. The room that ends up needing the hottest water (step 3) is the binding constraint — it dictates the whole system\'s supply temperature.' },

  { type: 'subtitle', text: '2 · Required mean radiator temperature' },
  { type: 'text', text: 'A radiator\'s output follows EN 442, with K = Q̇_nom/ΔT₅₀ⁿ and n ≈ 1.3. Inverting Q̇ = K·ΔTₘⁿ for the mean over-temperature needed to cover the demand:' },
  { type: 'latex', latex: '$ \\Delta T_m = \\Delta T_{50}\\left(\\dfrac{\\dot Q_{loss}}{\\dot Q_{nom}}\\right)^{1/n}, \\quad T_{avg} = T_{room} + \\Delta T_m $' },

  { type: 'subtitle', text: '3 · Supply temperature' },
  { type: 'text', text: 'With a design spread ΔT (here 5 K) the supply and return straddle the mean. The system supply is the largest value any room requires — the weakest room wins — capped at the heat pump\'s maximum:' },
  { type: 'latex', latex: '$ T_{supply} = \\max_i\\left(T_{avg,i} + \\tfrac{\\Delta T}{2}\\right) $' },
  { type: 'callout', text: 'Lower supply temperature → higher COP (≈2–3 % per K). Meeting the weakest room at the lowest sufficient supply is therefore the most efficient operating point.' },

  { type: 'subtitle', text: '4 · Required flow & presets' },
  { type: 'text', text: 'Each radiator must carry its demand at the design spread, which fixes the required flow. Valve presets are then set (with the auto-balancer) to deliver those flows within the current pump head:' },
  { type: 'latex', latex: '$ \\dot V_i = \\dfrac{\\dot Q_{loss,i}}{\\rho\\, c_p\\, \\Delta T} $' },
  { type: 'text', text: 'If a radiator is too small to meet its demand even at the maximum supply, or the pump head cannot push the required flow, that room is flagged in the report — the fix is a bigger radiator or a higher pump head, not a hotter system that wastes energy everywhere else.' },
];

const Block = ({ block }) => {
  switch (block.type) {
    case 'text':
      return <p className={styles.theoryText}>{block.text}</p>;
    case 'subtitle':
      return <p className={styles.sectionLabel} style={{ marginTop: 14 }}>{block.text}</p>;
    case 'latex':
      return (
        <div className={styles.theoryFormula}>
          <LatexRenderer latex={block.latex} />
        </div>
      );
    case 'callout':
      return <p className={styles.theoryCallout}>{block.text}</p>;
    default:
      return null;
  }
};

export const OptimizationTheoryDrawer = ({ open, onClose }) => (
  <Drawer
    title="Optimization — how the supply temperature & presets are derived"
    placement="right"
    width={672}
    open={open}
    onClose={onClose}
  >
    {BLOCKS.map((block, i) => (
      <Block key={i} block={block} />
    ))}
  </Drawer>
);
